import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CardMedia,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Rating,
  Backdrop,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";

import { OrderContext } from "../contexts/OrderContext";
import SetCard from "./SetCard";
import { SecureImage } from "./SecureImage";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const GuestProductList = ({
  linkType,
  linkName,
  linkId,
  mfr_id,
  handleAskLogin,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isSet, setIsSet] = useState(null);

  const location = useLocation();
  const stockPath = location.pathname;

  useEffect(() => {
    setIsSet(stockPath.includes("/set"));
  }, []);

  const [stockData, setStockData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const [storeInformation, setStoreInformation] = useState(null);

  // Fetch all stocks
  const fetchStockData = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/link/fetch-stock`,
        {
          linkId,
          mfr_id,
        },
      );

      const fetchedStock = response.data?.data || [];
      setStockData(fetchedStock.slice(0, 10));

      setStoreInformation(response?.data?.store_info);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStockData();
  }, [linkId]);

  const paginatedStock = stockData;

  // Loading Stock UI
  if (!Array.isArray(stockData) || stockData.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CircularProgress size={28} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Loading stock data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {/* App Bar */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          boxShadow: 1,
          mb: 0,
          height: isMobile ? 60 : 70,
          px: isMobile ? 2 : 5,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Store Logo */}
          <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
            <Box
              height={isMobile ? 45 : 60}
              width={isMobile ? 45 : 60}
              sx={{
                backgroundImage: `url(${storeInformation?.store_logo})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "50%",
                backgroundColor: "#f5f5f5",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                style={{
                  height: isMobile ? "60px" : "90px",
                  marginLeft: "8px",
                }}
                src={storeInformation?.store_logo}
                alt="logo"
              />
            </Box>

            {!isMobile && (
              <Typography sx={{ color: "red" }} variant="h6" fontWeight={600}>
                {storeInformation?.store_name}
                {storeInformation?.storePlace
                  ? `, ${storeInformation?.storePlace}`
                  : ""}
              </Typography>
            )}
          </Box>

          {/* Link Name */}
          <Typography sx={{ fontSize: 14 }}>{linkName}</Typography>

          {/* Cart Icon */}
          <Box
            onClick={handleAskLogin}
            sx={{ display: "flex", gap: 1, alignItems: "center" }}
          >
            <IconButton size="large">
              <ShoppingCartIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Heading */}
      <Typography
        sx={{
          textAlign: "center",
          mt: 1,
          fontSize: 18,
          color: "#fff",
          bgcolor: "#a30707d5",
          p: 1,
        }}
      >
        You're viewing limited number of items.{" "}
        <span
          style={{
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={handleAskLogin}
        >
          Log in
        </span>{" "}
        to view all.
      </Typography>

      {/* Stocks */}
      <Box
        display="grid"
        gridTemplateColumns={
          isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(264px, 1fr))"
        }
        justifyContent={isMobile ? "center" : "space-between"}
        gap={isMobile ? 1 : 2}
        sx={{ mt: 1, px: isMobile ? 1 : 4 }}
      >
        {!isSet &&
          (paginatedStock.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "gray", fontSize: "18px" }}>
              No Stock to show...
            </Box>
          ) : (
            <>
              {/* Dummy View More Box */}
              <Box
                sx={{
                  height: isMobile ? "200px" : "244px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",

                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  backdropFilter: "blur(2.5px)",
                  WebkitBackdropFilter: "blur(2.5px)",
                  mt: 1,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "background-color 0.25s ease", // smooth fade

                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)", // darker on hover
                  },
                }}
                onClick={handleAskLogin}
              >
                <DashboardRoundedIcon sx={{ fontSize: 96, fill: "#FFF" }} />
                <Typography
                  sx={{ fontSize: 16, color: "#FFF", fontWeight: 600 }}
                >
                  View All...
                </Typography>
              </Box>

              {/* Real Stocks */}
              {paginatedStock.map((stock, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    mt: 1,
                    position: "relative",
                    opacity: stock.out_of_stock ? 0.6 : 1,
                  }}
                >
                  {/* OUT OF STOCK BADGE */}
                  {stock.out_of_stock && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 0,
                        bgcolor: "#d32f2f",
                        color: "white",
                        px: 1.4,
                        py: 0.3,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        zIndex: 10,
                        clipPath:
                          "polygon(0 0, 100% 0, calc(100% - 10px) 50%, 100% 100%, 0 100%)",
                      }}
                    >
                      OUT OF STOCK
                    </Box>
                  )}

                  {/* Image */}
                  <Box
                    sx={{
                      height: isMobile ? "200px" : "244px",
                      borderRadius: 3,
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedItem(stock)}
                  >
                    <SecureImage
                      item={stock}
                      width="100%"
                      height="100%"
                      borderRadius={3}
                      objectFit="cover"
                    />
                  </Box>

                  {/* Card Content */}
                  <Box
                    sx={{
                      pt: 0.4,
                      height: "86px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      {/* LEFT DETAILS */}
                      <Box>
                        {stock.itemtype_name && (
                          <>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              sx={{ fontSize: "0.8rem", color: "#ba3800ff" }}
                            >
                              #{stock.tagno}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ fontSize: "0.7rem", color: "#000000ff" }}
                            >
                              {stock.itemtype_name}
                            </Typography>
                          </>
                        )}

                        {stock.gross && (
                          <Typography
                            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                          >
                            Gross: {parseFloat(stock.gross).toFixed(3)}g |{" "}
                            {parseFloat(stock.netwt).toFixed(3)}g
                          </Typography>
                        )}
                      </Box>

                      {/* RIGHT SIDE ADD TO CART BUTTON */}
                      <Box sx={{ px: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAskLogin}
                          sx={{
                            minWidth: 46,
                            width: 46,
                            height: 46,
                            borderRadius: "50%",
                            backgroundColor: "#FFF2DB",
                            border: `1px solid #995c004e`,
                            "&:hover": { backgroundColor: "#ffe0b3" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 0,
                          }}
                        >
                          <AddShoppingCartIcon
                            sx={{ fontSize: 20, color: "#a33c00ff" }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}

              {/* Dummy View More Box */}
              <Box
                sx={{
                  height: isMobile ? "200px" : "244px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",

                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  backdropFilter: "blur(2.5px)",
                  WebkitBackdropFilter: "blur(2.5px)",
                  mt: 1,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "background-color 0.25s ease", // smooth fade

                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)", // darker on hover
                  },
                }}
                onClick={handleAskLogin}
              >
                <DashboardRoundedIcon sx={{ fontSize: 96, fill: "#FFF" }} />
                <Typography
                  sx={{ fontSize: 16, color: "#FFF", fontWeight: 600 }}
                >
                  View All...
                </Typography>
              </Box>
            </>
          ))}

        {isSet &&
          (paginatedStock.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                color: "gray",
                fontSize: "18px",
              }}
            >
              No Stock to show...
            </Box>
          ) : (
            paginatedStock.map((stock, index) => (
              <SetCard key={index} stock={stock} />
            ))
          ))}
      </Box>

      {/* PRODUCT DETAILS DIALOG */}
      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => {
          setSelectedItem(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={() => {
              setSelectedItem(null);
            }}
            sx={{ position: "absolute", top: 8, right: 8, color: "black" }}
          >
            <CloseIcon />
          </IconButton>

          {/* Content */}
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
            {/* Image & Overlay Login Box */}
            <Box sx={{ flex: 3, position: "relative" }}>
              {/* Image */}
              <SecureImage
                item={selectedItem}
                width={"100%"}
                height={"100%"}
                borderRadius={2}
              />

              {/* Overlay Login Box */}
              <Box
                onClick={handleAskLogin}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "10px",
                  backgroundColor: "rgba(0, 0, 0, 0.45)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  backdropFilter: "blur(2px)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Login to view full image
                </Typography>

                <Button
                  // fullWidth
                  sx={{
                    backgroundColor: "#a30707",
                    color: "white",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#8b0505" },
                  }}
                  onClick={() => {
                    console.log("hello");
                    handleAskLogin();
                  }}
                >
                  Login
                </Button>
              </Box>
            </Box>

            {/* DETAILS & Add to Cart Button */}
            <Box sx={{ flex: 2, textAlign: "left" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedItem?.itemtype_name} ({selectedItem?.design_name})
              </Typography>

              <Typography sx={{ mt: 1, color: "#555" }}>
                Tag No: <b>{selectedItem?.tagno}</b>
              </Typography>

              <Typography sx={{ color: "#555" }}>
                Gross Wt: <b>{parseFloat(selectedItem?.gross).toFixed(3)}</b>
              </Typography>

              <Typography sx={{ color: "#555" }}>
                Net Wt: <b>{parseFloat(selectedItem?.netwt).toFixed(3)}</b>
              </Typography>

              <Typography sx={{ color: "#555" }}>
                Pcs: <b>{selectedItem?.pcs}</b>
              </Typography>

              <Typography sx={{ color: "#555" }}>
                Category: <b>{selectedItem?.category}</b>
              </Typography>

              {/* Add to Cart Button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  sx={{
                    fontSize: "11px",
                    padding: "5px 14px",
                    minWidth: "80px",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    background: "#ffd580",
                    color: "#5a3c00",
                    "&:hover": {
                      background: "#e6c076",
                    },
                  }}
                  onClick={handleAskLogin}
                >
                  Add to cart
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* LOGIN REQUIRED POPUP */}
      {/* <Dialog
        open={loginPopup}
        onClose={() => setLoginPopup(false)}
        PaperProps={{
          sx: {
            p: 3,
            borderRadius: 3,
            textAlign: "center",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)", // 👈 BLUR EFFECT
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 1, color: "#a30707" }}
          >
            Login Required
          </Typography>
          <Typography sx={{ mb: 2 }}>Please log in to continue.</Typography>

          <Button
            fullWidth
            sx={{
              backgroundColor: "#a30707",
              color: "white",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#8b0505" },
            }}
            onClick={() => {
              console.log("hello");
              handleAskLogin();
            }}
          >
            Login
          </Button>
        </DialogContent>
      </Dialog> */}
    </Box>
  );
};

export default GuestProductList;
